/**
 * Receivables Detail Report
 * 
 * URL resolution chains (all go line -> parent header):
 *   Invoice URL:          DOCHDRNO          -> SODOCUMENT.RECORD_URL
 *   BILLABLEGLENTRYKEY:   GLENTRY.BATCHNO   -> GLBATCH.RECORD_URL
 *   BILLABLETIMEENTRYKEY: TSENTRY.TIMESHEETKEY -> TIMESHEET.RECORD_URL
 *   BILLABLEDOCENTRYKEY:  APBILLITEM.RECORDKEY -> APBILL.RECORD_URL
 *                     or: PODOCUMENTENTRY.DOCHDRNO -> PODOCUMENT.RECORD_URL
 */

import { parseDate } from './reportHelpers';

const r2 = (v) => Math.round(v * 100) / 100;

const receivablesDetailReport = {
  id: 'receivables-detail',
  name: 'Receivables Detail',
  description: 'Project contract lines with expandable invoice drill-down. T&M lines show billable source documents with hyperlinks to headers.',
  category: 'Receivables',

  parameters: [
    {
      id: 'projectId', label: 'Project ID', type: 'text',
      default: '',
      description: 'Filter to a specific project (leave blank for all)'
    },
    {
      id: 'contractId', label: 'Contract ID', type: 'text',
      default: '',
      description: 'Filter to a specific project contract ID (leave blank for all)'
    },
    {
      id: 'includeClosed', label: 'Include Closed Contracts', type: 'checkbox',
      default: false,
      description: 'Include contract lines from closed/completed contracts'
    },
    {
      id: 'includeStandalone', label: 'Include Non-Contract AR', type: 'checkbox',
      default: true,
      description: 'Include AR invoices not linked to project contracts'
    },
  ],

  queries: [
    {
      id: 'contractLines', object: 'PROJECTCONTRACTLINE',
      fields: [
        'RECORDNO', 'PROJECTCONTRACTLINEID', 'PROJECTCONTRACTID', 'PROJECTCONTRACTNAME',
        'NAME', 'DESCRIPTION', 'BILLINGTYPE',
        'ORIGINALPRICE', 'TOTALREVISEDPRICE', 'BILLEDPRICE', 'BILLEDNETRETAINAGE',
        'BALANCETOBILL', 'RETAINAGEHELD', 'RETAINAGERELEASED', 'RETAINAGEBALANCE',
        'RETAINAGEPERCENTAGE',
        'TASKID', 'TASKNAME', 'COSTTYPEID', 'COSTTYPENAME',
        'PROJECTID', 'PROJECTNAME', 'CUSTOMERID', 'CUSTOMERNAME',
        'STATUS', 'RECORD_URL', 'MEGAENTITYID',
      ],
      buildFilter: (params) => {
        const parts = [];
        if (params.projectId) parts.push("PROJECTID = '" + params.projectId + "'");
        if (params.contractId) parts.push("PROJECTCONTRACTID = '" + params.contractId + "'");
        if (!params.includeClosed) parts.push("STATUS = 'active'");
        return parts.length > 0 ? parts.join(' AND ') : "STATUS = 'active'";
      }
    },
    {
      id: 'invoiceLines', object: 'SODOCUMENTENTRY',
      fields: [
        'RECORDNO', 'PROJECTCONTRACTLINEKEY', 'PROJECTCONTRACTLINEID',
        'PROJECTCONTRACTID', 'PROJECTCONTRACTNAME',
        'DOCHDRNO', 'DOCHDRID', 'LINE_NO',
        'TRX_REVISEDVALUE', 'TOTAL', 'TRX_AMOUNTRETAINED', 'AMOUNTRETAINED',
        'COMPLETEDTHISPERIOD', 'PRIORAPPLICATIONAMT', 'TOTALCOMPLETEDTODATE',
        'COSTTYPEID', 'COSTTYPENAME', 'TASKID', 'TASKNAME',
        'PCBLBILLINGTYPE', 'ITEMDESC', 'ITEMID', 'ITEMNAME',
        'EMPLOYEEID', 'EMPLOYEENAME', 'QUANTITY',
        'BILLABLEGLENTRYKEY', 'BILLABLETIMEENTRYKEY',
        'BILLABLEDOCENTRYKEY', 'BILLABLEPRENTRYKEY',
        'PROJECTID', 'PROJECTNAME',
        'SODOCUMENT.DOCID', 'SODOCUMENT.MEGAENTITYID',
        'SODOCUMENT.WHENCREATED', 'SODOCUMENT.STATE',
      ],
      buildFilter: (params) => {
        const parts = ['PROJECTCONTRACTLINEKEY > 0'];
        if (params.projectId) parts.push("PROJECTID = '" + params.projectId + "'");
        if (params.contractId) parts.push("PROJECTCONTRACTID = '" + params.contractId + "'");
        return parts.join(' AND ');
      }
    },

    // ---- INVOICE HEADER URLs (SODOCUMENT) ----
    {
      id: 'soDocuments', object: 'SODOCUMENT',
      fields: ['RECORDNO', 'RECORD_URL', 'MEGAENTITYID'],
      buildFilter: (params) => {
        const parts = [];
        if (params.projectId) parts.push("PROJECT = '" + params.projectId + "'");
        return parts.length > 0 ? parts.join(' AND ') : 'RECORDNO > 0';
      },
      conditional: (params) => !!params.projectId || !!params.contractId,
    },

    // ---- GL: GLENTRY -> GLBATCH ----
    {
      id: 'glEntries', object: 'GLENTRY',
      fields: [
        'RECORDNO', 'BATCHNO', 'DESCRIPTION',
        'EMPLOYEEID', 'EMPLOYEENAME', 'COSTTYPEID', 'COSTTYPENAME',
      ],
      buildFilter: (params) => {
        const parts = [];
        if (params.projectId) parts.push("PROJECTID = '" + params.projectId + "'");
        return parts.length > 0 ? parts.join(' AND ') : 'RECORDNO > 0';
      },
      conditional: (params) => !!params.projectId || !!params.contractId,
    },
    {
      id: 'glBatches', object: 'GLBATCH',
      fields: ['RECORDNO', 'RECORD_URL', 'MEGAENTITYID'],
      buildFilter: (params) => 'RECORDNO > 0',
      conditional: (params) => !!params.projectId || !!params.contractId,
    },

    // ---- TIMESHEET: TIMESHEETENTRY -> TIMESHEET ----
    {
      id: 'tsEntries', object: 'TIMESHEETENTRY',
      fields: [
        'RECORDNO', 'TIMESHEETKEY', 'DESCRIPTION', 'QTY', 'ENTRYDATE',
        'EMPLOYEEID', 'EMPLOYEENAME', 'COSTTYPEID', 'COSTTYPENAME',
      ],
      buildFilter: (params) => {
        const parts = [];
        if (params.projectId) parts.push("PROJECTID = '" + params.projectId + "'");
        return parts.length > 0 ? parts.join(' AND ') : 'RECORDNO > 0';
      },
      conditional: (params) => !!params.projectId || !!params.contractId,
    },
    {
      id: 'timesheets', object: 'TIMESHEET',
      fields: ['RECORDNO', 'RECORD_URL', 'MEGAENTITYID'],
      buildFilter: (params) => 'RECORDNO > 0',
      conditional: (params) => !!params.projectId || !!params.contractId,
    },

    // ---- AP BILL: APBILLITEM -> APBILL ----
    {
      id: 'apBillItems', object: 'APBILLITEM',
      fields: ['RECORDNO', 'RECORDKEY', 'VENDORNAME', 'COSTTYPEID'],
      buildFilter: (params) => {
        const parts = [];
        if (params.projectId) parts.push("PROJECTID = '" + params.projectId + "'");
        return parts.length > 0 ? parts.join(' AND ') : 'RECORDNO > 0';
      },
      conditional: (params) => !!params.projectId || !!params.contractId,
    },
    {
      id: 'apBills', object: 'APBILL',
      fields: ['RECORDNO', 'RECORD_URL', 'MEGAENTITYID', 'DOCNUMBER', 'VENDORNAME'],
      buildFilter: (params) => 'RECORDNO > 0',
      conditional: (params) => !!params.projectId || !!params.contractId,
    },

    // ---- PO DOCUMENT: PODOCUMENTENTRY -> PODOCUMENT ----
    {
      id: 'poDocEntries', object: 'PODOCUMENTENTRY',
      fields: ['RECORDNO', 'DOCHDRID', 'VENDORID', 'VENDORNAME', 'COSTTYPEID'],
      buildFilter: (params) => {
        const parts = [];
        if (params.projectId) parts.push("PROJECTID = '" + params.projectId + "'");
        return parts.length > 0 ? parts.join(' AND ') : 'RECORDNO > 0';
      },
      conditional: (params) => !!params.projectId || !!params.contractId,
    },
    {
      id: 'poDocuments', object: 'PODOCUMENT',
      fields: ['RECORDNO', 'RECORD_URL', 'MEGAENTITYID'],
      buildFilter: (params) => 'RECORDNO > 0',
      conditional: (params) => !!params.projectId || !!params.contractId,
    },

    // ---- STANDALONE AR ----
    {
      id: 'standaloneInv', object: 'ARINVOICE',
      fields: [
        'RECORDNO', 'RECORDID', 'CUSTOMERID', 'CUSTOMERNAME',
        'WHENCREATED', 'WHENDUE', 'TOTALENTERED', 'TOTALDUE', 'TOTALRETAINED',
        'STATE', 'RECORD_URL', 'MEGAENTITYID', 'DESCRIPTION',
      ],
      buildFilter: (params) => {
        const parts = ['TOTALDUE != 0'];
        parts.push("STATE NOT IN ('Reversed','Reversal','Voided','Deleted')");
        return parts.join(' AND ');
      },
      conditional: (params) => params.includeStandalone,
    },
    {
      id: 'standaloneLines', object: 'ARINVOICEITEM',
      fields: ['ARINVOICE.RECORDNO', 'PROJECTID', 'PROJECTNAME', 'AMOUNT', 'TOTALPAID', 'TRX_AMOUNTRETAINED'],
      buildFilter: (params) => {
        const parts = ['ARINVOICE.TOTALDUE != 0'];
        if (params.projectId) parts.push("PROJECTID = '" + params.projectId + "'");
        return parts.join(' AND ');
      },
      conditional: (params) => params.includeStandalone,
    },
  ],

  additionalQueries: [],
  optionalColumns: [],

  process: (datasets, params) => {
    const contractLines = datasets.contractLines || [];
    const invoiceLines = datasets.invoiceLines || [];
    const soDocuments = datasets.soDocuments || [];
    const glEntries = datasets.glEntries || [];
    const glBatches = datasets.glBatches || [];
    const tsEntries = datasets.tsEntries || [];
    const timesheets = datasets.timesheets || [];
    const apBillItems = datasets.apBillItems || [];
    const apBills = datasets.apBills || [];
    const poDocEntries = datasets.poDocEntries || [];
    const poDocuments = datasets.poDocuments || [];
    const standaloneInv = datasets.standaloneInv || [];
    const standaloneLines = datasets.standaloneLines || [];
    const projectFilter = (params.projectId || '').trim();

    // Standalone project lookup
    const invProjectMap = {};
    for (const line of standaloneLines) {
      const parentRN = String(line['ARINVOICE.RECORDNO'] || '').trim();
      if (!parentRN) continue;
      const pid = String(line.PROJECTID || '').trim();
      if (pid && !invProjectMap[parentRN]) {
        invProjectMap[parentRN] = { projectId: pid, projectName: String(line.PROJECTNAME || '').trim() };
      }
    }

    // ============ BUILD HEADER URL MAPS ============

    // SODOCUMENT: RECORDNO -> RECORD_URL (for invoice links)
    const soUrlMap = {};
    for (const so of soDocuments) {
      const rn = String(so.RECORDNO || '').trim();
      if (rn && so.RECORD_URL) soUrlMap[rn] = so.RECORD_URL;
    }

    // GLBATCH: RECORDNO -> RECORD_URL
    const glBatchUrlMap = {};
    for (const b of glBatches) {
      const rn = String(b.RECORDNO || '').trim();
      if (rn && b.RECORD_URL) glBatchUrlMap[rn] = b.RECORD_URL;
    }

    // TIMESHEET: RECORDNO -> RECORD_URL
    const tsUrlMap = {};
    for (const t of timesheets) {
      const rn = String(t.RECORDNO || '').trim();
      if (rn && t.RECORD_URL) tsUrlMap[rn] = t.RECORD_URL;
    }

    // APBILL: RECORDNO -> { url, vendor, docNumber }
    const apBillUrlMap = {};
    for (const b of apBills) {
      const rn = String(b.RECORDNO || '').trim();
      if (rn && b.RECORD_URL) apBillUrlMap[rn] = { url: b.RECORD_URL, vendor: b.VENDORNAME || '', doc: b.DOCNUMBER || '' };
    }

    // PODOCUMENT: RECORDNO -> RECORD_URL
    const poDocUrlMap = {};
    for (const p of poDocuments) {
      const rn = String(p.RECORDNO || '').trim();
      if (rn && p.RECORD_URL) poDocUrlMap[rn] = p.RECORD_URL;
    }

    // ============ BUILD LINE -> HEADER URL CHAINS ============

    // GLENTRY.RECORDNO -> GLBATCH URL (via BATCHNO)
    const glSourceMap = {};
    for (const gl of glEntries) {
      const rn = String(gl.RECORDNO || '').trim();
      if (!rn) continue;
      const batchNo = String(gl.BATCHNO || '').trim();
      glSourceMap[rn] = {
        url: glBatchUrlMap[batchNo] || '',
        employee: gl.EMPLOYEENAME || '',
        costType: gl.COSTTYPEID || '',
      };
    }

    // TIMESHEETENTRY.RECORDNO -> TIMESHEET URL (via TIMESHEETKEY)
    const tsSourceMap = {};
    for (const ts of tsEntries) {
      const rn = String(ts.RECORDNO || '').trim();
      if (!rn) continue;
      const tsKey = String(ts.TIMESHEETKEY || '').trim();
      tsSourceMap[rn] = {
        url: tsUrlMap[tsKey] || '',
        employee: ts.EMPLOYEENAME || '',
        qty: parseFloat(ts.QTY) || 0,
        costType: ts.COSTTYPEID || '',
      };
    }

    // APBILLITEM.RECORDNO -> APBILL URL (via RECORDKEY)
    const apSourceMap = {};
    for (const ap of apBillItems) {
      const rn = String(ap.RECORDNO || '').trim();
      if (!rn) continue;
      const billKey = String(ap.RECORDKEY || '').trim();
      const billInfo = apBillUrlMap[billKey] || {};
      apSourceMap[rn] = {
        url: billInfo.url || '',
        vendor: ap.VENDORNAME || billInfo.vendor || '',
        docNumber: billInfo.doc || '',
      };
    }

    // PODOCUMENTENTRY.RECORDNO -> PODOCUMENT URL (via DOCHDRID which contains the parent doc identifier)
    // Note: DOCHDRID is a string like "Subcontracts-SUB-000110", not a RECORDNO.
    // We need to match via the numeric DOCHDRNO-equivalent. Let me use a different approach:
    // Actually for PODOCUMENTENTRY, the parent PODOCUMENT RECORDNO might be in a different field.
    // Let's build by iterating and checking what we have.
    const poSourceMap = {};
    for (const po of poDocEntries) {
      const rn = String(po.RECORDNO || '').trim();
      if (!rn) continue;
      // DOCHDRID on PODOCUMENTENTRY contains something like the parent doc number
      // We need to find the PODOCUMENT whose RECORDNO matches
      // Actually the simpler approach: just store vendor info; URL will come from apSourceMap if it matches
      poSourceMap[rn] = {
        vendor: po.VENDORNAME || po.VENDORID || '',
        costType: po.COSTTYPEID || '',
      };
    }

    // ============ GROUP INVOICE LINES BY CONTRACT LINE ============
    const linesByContract = {};

    for (const il of invoiceLines) {
      const clKey = String(il.PROJECTCONTRACTLINEKEY || '').trim();
      if (!clKey) continue;

      const invoiceRN = String(il.DOCHDRNO || '').trim();
      if (!invoiceRN) continue;

      if (!linesByContract[clKey]) linesByContract[clKey] = {};
      if (!linesByContract[clKey][invoiceRN]) {
        // Get invoice URL from our SODOCUMENT map using DOCHDRNO
        const invoiceUrl = soUrlMap[invoiceRN] || '';

        linesByContract[clKey][invoiceRN] = {
          docId: il['SODOCUMENT.DOCID'] || il.DOCHDRID || '',
          url: invoiceUrl,
          entity: il['SODOCUMENT.MEGAENTITYID'] || '',
          date: il['SODOCUMENT.WHENCREATED'] || '',
          state: il['SODOCUMENT.STATE'] || '',
          lines: [],
          thisLineTotal: 0,
          thisLineRetainage: 0,
        };
      }

      const value = parseFloat(il.TRX_REVISEDVALUE) || parseFloat(il.TOTAL) || 0;
      const retained = parseFloat(il.TRX_AMOUNTRETAINED) || 0;

      linesByContract[clKey][invoiceRN].lines.push(il);
      linesByContract[clKey][invoiceRN].thisLineTotal += value;
      linesByContract[clKey][invoiceRN].thisLineRetainage += retained;
    }

    // ============ BUILD REPORT ROWS ============
    const columns = [
      'Project', 'Contract', 'Line', 'Description', 'Billing Type',
      'Original Amount', 'Revised Amount', 'Billed', 'Billed Net Ret.',
      'Balance to Bill', 'Retainage Held', 'Retainage Released', 'Retainage Balance',
    ];

    const formatRules = {
      'Original Amount': { type: 'currency' },
      'Revised Amount': { type: 'currency' },
      'Billed': { type: 'currency' },
      'Billed Net Ret.': { type: 'currency' },
      'Balance to Bill': { type: 'currency' },
      'Retainage Held': { type: 'currency' },
      'Retainage Released': { type: 'currency' },
      'Retainage Balance': { type: 'currency' },
      'Description': { type: 'hyperlink', urlKey: '_descUrl' },
    };

    const reportRows = [];
    const detailRowIndices = [];
    const rowOutlineLevels = [];

    let totalOriginal = 0, totalRevised = 0, totalBilled = 0, totalBilledNet = 0;
    let totalBalance = 0, totalRetHeld = 0, totalRetReleased = 0, totalRetBalance = 0;

    const emptyRow = () => {
      const row = {};
      for (const col of columns) row[col] = '';
      row['_descUrl'] = '';
      return row;
    };

    const sortedCL = [...contractLines].sort((a, b) => {
      const cmp1 = (a.PROJECTID || '').localeCompare(b.PROJECTID || '');
      if (cmp1 !== 0) return cmp1;
      const cmp2 = (a.PROJECTCONTRACTID || '').localeCompare(b.PROJECTCONTRACTID || '');
      if (cmp2 !== 0) return cmp2;
      return (parseInt(a.PROJECTCONTRACTLINEID) || 0) - (parseInt(b.PROJECTCONTRACTLINEID) || 0);
    });

    for (const cl of sortedCL) {
      const clKey = String(cl.RECORDNO || '').trim();

      const originalAmt = r2(parseFloat(cl.ORIGINALPRICE) || 0);
      const revisedAmt = r2(parseFloat(cl.TOTALREVISEDPRICE) || 0);
      const billed = r2(parseFloat(cl.BILLEDPRICE) || 0);
      const billedNet = r2(parseFloat(cl.BILLEDNETRETAINAGE) || 0);
      const balanceToBill = r2(parseFloat(cl.BALANCETOBILL) || 0);
      const retHeld = r2(parseFloat(cl.RETAINAGEHELD) || 0);
      const retReleased = r2(parseFloat(cl.RETAINAGERELEASED) || 0);
      const retBalance = r2(parseFloat(cl.RETAINAGEBALANCE) || 0);

      totalOriginal += originalAmt;
      totalRevised += revisedAmt;
      totalBilled += billed;
      totalBilledNet += billedNet;
      totalBalance += balanceToBill;
      totalRetHeld += retHeld;
      totalRetReleased += retReleased;
      totalRetBalance += retBalance;

      const projectDisplay = cl.PROJECTID
        ? (cl.PROJECTNAME ? cl.PROJECTID + ' - ' + cl.PROJECTNAME : cl.PROJECTID)
        : '';

      // Level 0: Contract line
      reportRows.push({
        'Project': projectDisplay,
        'Contract': cl.PROJECTCONTRACTID || '',
        'Line': cl.PROJECTCONTRACTLINEID || '',
        'Description': cl.DESCRIPTION || cl.NAME || '',
        'Billing Type': cl.BILLINGTYPE || '',
        'Original Amount': originalAmt,
        'Revised Amount': revisedAmt,
        'Billed': billed,
        'Billed Net Ret.': billedNet,
        'Balance to Bill': balanceToBill,
        'Retainage Held': retHeld,
        'Retainage Released': retReleased,
        'Retainage Balance': retBalance,
        '_descUrl': cl.RECORD_URL || '',
      });
      rowOutlineLevels.push(0);

      // Level 1: Invoices
      const invoices = linesByContract[clKey] || {};
      const invoicesSorted = Object.entries(invoices).sort(([, a], [, b]) =>
        (a.date || '').localeCompare(b.date || '')
      );

      for (const [invoiceRN, invData] of invoicesSorted) {
        const docId = invData.docId || '';
        const invoiceNo = docId.includes('-') ? docId.split('-').pop() : docId;
        const lineTotal = r2(invData.thisLineTotal);
        const lineRet = r2(invData.thisLineRetainage);
        const invoiceUrl = invData.url || '';

        const invRow = emptyRow();
        invRow['Description'] = 'Invoice ' + invoiceNo + ' (this line: $' + lineTotal.toLocaleString('en-US', { minimumFractionDigits: 2 }) + ')';
        invRow['Billed'] = lineTotal;
        invRow['Retainage Held'] = lineRet;
        invRow['Billed Net Ret.'] = r2(lineTotal - lineRet);
        invRow['_descUrl'] = invoiceUrl;

        reportRows.push(invRow);
        detailRowIndices.push(reportRows.length - 1);
        rowOutlineLevels.push(1);

        // Level 2: Line items
        const sortedLines = [...invData.lines].sort((a, b) =>
          (parseInt(a.LINE_NO) || 0) - (parseInt(b.LINE_NO) || 0)
        );

        for (const line of sortedLines) {
          const lineValue = r2(parseFloat(line.TRX_REVISEDVALUE) || parseFloat(line.TOTAL) || 0);
          const lineRetained = r2(parseFloat(line.TRX_AMOUNTRETAINED) || 0);

          // Resolve source URL through two-hop chains
          let sourceLabel = '';
          let sourceUrl = '';

          const glKey = String(line.BILLABLEGLENTRYKEY || '').trim();
          const tsKey = String(line.BILLABLETIMEENTRYKEY || '').trim();
          const docKey = String(line.BILLABLEDOCENTRYKEY || '').trim();
          const prKey = String(line.BILLABLEPRENTRYKEY || '').trim();

          if (glKey && glSourceMap[glKey]) {
            const src = glSourceMap[glKey];
            sourceLabel = 'GL: ' + (src.employee || src.costType || 'Journal');
            sourceUrl = src.url;
          } else if (tsKey && tsSourceMap[tsKey]) {
            const src = tsSourceMap[tsKey];
            sourceLabel = 'TS: ' + (src.employee || 'Timesheet') + ' (' + src.qty + 'h)';
            sourceUrl = src.url;
          } else if (docKey && apSourceMap[docKey]) {
            // BILLABLEDOCENTRYKEY matched an APBILLITEM
            const src = apSourceMap[docKey];
            sourceLabel = 'Bill: ' + (src.vendor || src.docNumber || docKey);
            sourceUrl = src.url;
          } else if (docKey && poSourceMap[docKey]) {
            // BILLABLEDOCENTRYKEY matched a PODOCUMENTENTRY
            const src = poSourceMap[docKey];
            sourceLabel = 'PO: ' + (src.vendor || docKey);
            // PO URL lookup would need parent RECORDNO - TODO if needed
            sourceUrl = '';
          } else if (glKey) {
            sourceLabel = 'GL #' + glKey;
          } else if (tsKey) {
            sourceLabel = 'TS #' + tsKey;
          } else if (docKey) {
            sourceLabel = 'Doc #' + docKey;
          } else if (prKey) {
            sourceLabel = 'Exp #' + prKey;
          }

          const descParts = [];
          if (line.COSTTYPENAME || line.COSTTYPEID) descParts.push(line.COSTTYPENAME || line.COSTTYPEID);
          if (line.EMPLOYEENAME) descParts.push(line.EMPLOYEENAME);
          if (line.ITEMDESC && !descParts.includes(line.ITEMDESC)) descParts.push(line.ITEMDESC);
          if (sourceLabel) descParts.push('[' + sourceLabel + ']');

          const lineDesc = descParts.length > 0
            ? '    ' + descParts.join(' | ')
            : '    Line ' + (line.LINE_NO || '');

          const detailRow = emptyRow();
          detailRow['Description'] = lineDesc;
          detailRow['Billed'] = lineValue;
          detailRow['Retainage Held'] = lineRetained;
          detailRow['Billed Net Ret.'] = r2(lineValue - lineRetained);
          // Source header URL, fallback to invoice URL
          detailRow['_descUrl'] = sourceUrl || invoiceUrl;

          reportRows.push(detailRow);
          detailRowIndices.push(reportRows.length - 1);
          rowOutlineLevels.push(2);
        }
      }
    }

    // ============ STANDALONE AR INVOICES ============
    if (params.includeStandalone && standaloneInv.length > 0) {
      const sectionRow = emptyRow();
      sectionRow['Project'] = '--- NON-CONTRACT AR INVOICES ---';
      reportRows.push(sectionRow);
      rowOutlineLevels.push(0);

      let filteredStandalone = standaloneInv;
      if (projectFilter) {
        const matchingInvRNs = new Set();
        for (const line of standaloneLines) {
          if (String(line.PROJECTID || '').trim() === projectFilter) {
            matchingInvRNs.add(String(line['ARINVOICE.RECORDNO'] || '').trim());
          }
        }
        filteredStandalone = standaloneInv.filter(inv =>
          matchingInvRNs.has(String(inv.RECORDNO || '').trim())
        );
      }

      const sortedStandalone = [...filteredStandalone].sort((a, b) => {
        const aProj = (invProjectMap[String(a.RECORDNO || '').trim()] || {}).projectId || '';
        const bProj = (invProjectMap[String(b.RECORDNO || '').trim()] || {}).projectId || '';
        const cmp = aProj.localeCompare(bProj);
        if (cmp !== 0) return cmp;
        return (a.WHENCREATED || '').localeCompare(b.WHENCREATED || '');
      });

      for (const inv of sortedStandalone) {
        const totalDue = r2(parseFloat(inv.TOTALDUE) || 0);
        const totalEntered = r2(parseFloat(inv.TOTALENTERED) || 0);
        const totalRetained = r2(parseFloat(inv.TOTALRETAINED) || 0);
        const projInfo = invProjectMap[String(inv.RECORDNO || '').trim()] || {};
        const projectDisplay = projInfo.projectId
          ? (projInfo.projectName ? projInfo.projectId + ' - ' + projInfo.projectName : projInfo.projectId)
          : '(No Project)';

        reportRows.push({
          'Project': projectDisplay,
          'Contract': '',
          'Line': '',
          'Description': (inv.RECORDID || 'Invoice') + ' - ' + (inv.CUSTOMERNAME || inv.CUSTOMERID || ''),
          'Billing Type': '',
          'Original Amount': totalEntered,
          'Revised Amount': '',
          'Billed': totalEntered,
          'Billed Net Ret.': r2(totalEntered - totalRetained),
          'Balance to Bill': totalDue,
          'Retainage Held': totalRetained,
          'Retainage Released': '',
          'Retainage Balance': '',
          '_descUrl': inv.RECORD_URL || '',
        });
        rowOutlineLevels.push(0);
      }
    }

    // ============ TOTAL ============
    reportRows.push({
      'Project': 'TOTAL', 'Contract': '', 'Line': '', 'Description': '', 'Billing Type': '',
      'Original Amount': r2(totalOriginal), 'Revised Amount': r2(totalRevised),
      'Billed': r2(totalBilled), 'Billed Net Ret.': r2(totalBilledNet),
      'Balance to Bill': r2(totalBalance), 'Retainage Held': r2(totalRetHeld),
      'Retainage Released': r2(totalRetReleased), 'Retainage Balance': r2(totalRetBalance),
      '_descUrl': '',
    });
    rowOutlineLevels.push(0);

    return {
      columns, data: reportRows, formatRules, detailRowIndices, rowOutlineLevels,
      summary: { lines: [
        sortedCL.length + ' contract line(s), ' + invoiceLines.length + ' invoice line(s)',
        'Total Billed: $' + r2(totalBilled).toLocaleString('en-US', { minimumFractionDigits: 2 }),
        'Balance to Bill: $' + r2(totalBalance).toLocaleString('en-US', { minimumFractionDigits: 2 }),
        'Retainage Balance: $' + r2(totalRetBalance).toLocaleString('en-US', { minimumFractionDigits: 2 }),
      ]},
    };
  },

  formatRules: {},
};

export default receivablesDetailReport;